import { subscribe, useSnapshot } from 'valtio';
import { useAuth } from '@context/auth/base';
import { useSafeLayoutEffect } from '@hooks/useSafeLayoutEffect';
import type { FeaturedStatusEnum } from './schema';
import { LandingFeaturedStatusSchema } from './schema';
import { LandingFeaturedStatusPersistence, landingFeaturedStatusState } from './store';

if (typeof window !== 'undefined') {
  LandingFeaturedStatusPersistence.initialValue
    .then(value => {
      if (!value) return;
      Object.assign(landingFeaturedStatusState, value);
    })
    // eslint-disable-next-line no-console
    .catch(console.error);

  subscribe(
    landingFeaturedStatusState,
    () => {
      // Debounce storage updates to minimize CPU Usage on frequent state updates
      const pendingStorageSet = setTimeout(() => {
        const parseToStore = LandingFeaturedStatusSchema.safeParse(landingFeaturedStatusState);
        // This way we ensure that we are inserting valid data into the storage
        // reduces sentry noise too
        if (parseToStore.success) {
          LandingFeaturedStatusPersistence.set(parseToStore.data);
        }
        clearTimeout(pendingStorageSet);
      }, 0);
    },
    true,
  );
}

const updateUpcoming = (status: FeaturedStatusEnum) => {
  landingFeaturedStatusState.upcoming = status;
};

const updateRecent = (status: FeaturedStatusEnum) => {
  landingFeaturedStatusState.recent = status;
};

export function useFeaturedStatus() {
  const { isAuthenticatedWithAPI, isConnectingAPIUser } = useAuth();
  const { upcoming, recent } = useSnapshot(landingFeaturedStatusState, {
    sync: true,
  });

  /**
   * We need to reset following tab if user is not authenticated
   */
  useSafeLayoutEffect(() => {
    if (isConnectingAPIUser) return;

    if (isAuthenticatedWithAPI) return;

    if (landingFeaturedStatusState.recent === 'FOLLOWING')
      landingFeaturedStatusState.recent = 'HOT';

    if (landingFeaturedStatusState.upcoming === 'FOLLOWING')
      landingFeaturedStatusState.upcoming = 'HOT';
  }, [isAuthenticatedWithAPI, isConnectingAPIUser]);

  return { upcoming, recent, updateUpcoming, updateRecent };
}
